import { useEffect, useRef, useState } from 'react';
import { api, useOwnerMutation, useOwnerQuery } from '../lib/api';
import { useFeeds } from '../lib/library';
import { importBatches, MAX_OPML_BYTES, parseOpml, type ImportRow } from '../lib/opml';
import { toast } from '../lib/store';
import { Icon } from './Icon';

export function Opml() {
  const text = useOwnerQuery(api.feeds.opml, {});
  const importSources = useOwnerMutation(api.feeds.importSources);
  const feeds = useFeeds();
  const fileRef = useRef<HTMLInputElement>(null);
  const attempt = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const fileGeneration = useRef(0);
  const [rows, setRows] = useState<ImportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; attempt.current?.abort(); }; }, []);

  const exportFile = () => {
    if (!text) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/x-opml' }));
    a.download = 'vladrss-sources.opml'; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const read = async (file: File) => {
    const generation = ++fileGeneration.current;
    setError(null); setRows(null); setProgress(null);
    try {
      if (file.size > MAX_OPML_BYTES) throw new Error('Choose an OPML file no larger than 2 MB.');
      const parsed = parseOpml(await file.text(), (feeds ?? []).filter((f) => !f.deleted).map((f) => f.url));
      if (mounted.current && generation === fileGeneration.current) setRows(parsed);
    } catch (e) { if (mounted.current && generation === fileGeneration.current) setError(e instanceof Error ? e.message : "Couldn't read this OPML file. Choose it again."); }
  };
  const follow = async () => {
    if (attempt.current) return; // includes a second click before React has rendered disabled
    const selected = rows?.filter((r) => r.on && !r.have).map(({ url, title, category }) => ({ url, title, category })) ?? [];
    if (!selected.length) return;
    const controller = new AbortController(); attempt.current = controller;
    let done = 0;
    setBusy(true); setError(null); setProgress({ done, total: selected.length });
    try {
      const complete = await importBatches(selected, (batch) => importSources({ sources: batch }), (batch, count) => {
        done = count;
        if (!mounted.current) return;
        const urls = new Set(batch.map((r) => r.url));
        setRows((current) => current?.map((r) => urls.has(r.url) ? { ...r, have: true, on: false } : r) ?? null);
        setProgress({ done, total: selected.length });
      }, controller.signal);
      if (mounted.current) {
        if (complete) { setRows(null); setProgress(null); toast('Import complete'); }
        else setError('Import stopped. Your remaining selection is kept.');
      }
    } catch {
      if (mounted.current) setError(`Import paused after ${done} of ${selected.length} sources. Your remaining selection is kept; retry to continue.`);
    } finally {
      attempt.current = null;
      if (mounted.current) setBusy(false);
    }
  };
  const remaining = rows?.filter((r) => r.on && !r.have).length ?? 0;
  return (
    <div className="drop">
      <Icon name="upload" />
      <div className="t"><b>Moving from another reader?</b> Import an OPML file; sources arrive in their categories.</div>
      <input ref={fileRef} type="file" accept=".opml,.xml,text/xml" hidden disabled={busy || feeds === undefined} onChange={(e) => { const f = e.target.files?.[0]; if (f) void read(f); e.target.value = ''; }} />
      <button className="btn" disabled={busy || feeds === undefined} onClick={() => fileRef.current?.click()}>Import OPML</button>
      <button className="btn ghost" onClick={exportFile} disabled={!text}><Icon name="download" size={16} />Export</button>
      {error && <p role="alert">{error}</p>}
      {progress && <p role="status">{progress.done} of {progress.total} sources imported</p>}
      {rows && <div className="imp">
        {rows.map((r, i) => <label key={r.url} className={`imp-r${r.have ? ' have' : ''}`}>
          <input type="checkbox" disabled={r.have || busy} checked={r.on} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, on: e.target.checked } : x))} />
          <span className="t"><b>{r.title}</b><small>{new URL(r.url).hostname} · {r.category}</small></span>{r.have && <span className="tag">Following</span>}
        </label>)}
        <div className="acts2">
          <button className="btn primary" disabled={busy || !remaining} onClick={() => { void follow(); }}>{busy ? 'Importing…' : `${error ? 'Retry' : 'Follow'} ${remaining} new`}</button>
          <button className="btn" onClick={() => { if (busy) attempt.current?.abort(); else { setRows(null); setError(null); setProgress(null); } }}>{busy ? 'Stop importing' : 'Cancel'}</button>
        </div>
      </div>}
    </div>
  );
}
